import { useEffect, useState } from "react";

import { Button, Modal, TextField } from "../../../../ui/index.ts";
import type { DepartmentRow } from "../../types.ts";
import styles from "./department-form-modal.module.css";

export interface DepartmentFormModalProps {
  open: boolean;
  department: DepartmentRow | null;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (name: string) => Promise<boolean | string>;
}

export function DepartmentFormModal({
  open,
  department,
  submitting,
  onClose,
  onSubmit,
}: DepartmentFormModalProps) {
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | undefined>();
  const isEdit = department != null;

  useEffect(() => {
    if (!open) {
      setName("");
      setNameError(undefined);
      return;
    }
    setName(department?.name ?? "");
    setNameError(undefined);
  }, [open, department]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setNameError("Department name is required");
      return;
    }
    if (/^[!@#$%^&*(),.?":{}|<>]/.test(trimmed)) {
      setNameError("Name cannot start with a special character");
      return;
    }
    setNameError(undefined);
    const result = await onSubmit(trimmed);
    if (result === true) {
      onClose();
    } else if (typeof result === "string") {
      setNameError(result);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? "Update Department" : "Add Department"}
      size="md"
      footer={
        <div className={styles.footer}>
          <Button variant="light-grey" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="accent"
            onClick={() => void handleSubmit()}
            disabled={submitting || (isEdit && name.trim() === department?.name)}
          >
            {submitting ? "Saving..." : isEdit ? "Update" : "Submit"}
          </Button>
        </div>
      }
    >
      <div className={styles.body}>
        <TextField
          id="department-name"
          label="Name"
          required
          value={name}
          maxLength={150}
          onChange={(e) => {
            setName(e.target.value);
            if (nameError) setNameError(undefined);
          }}
          error={nameError}
          placeholder="Enter Department Name"
        />
      </div>
    </Modal>
  );
}
